import React, { useEffect, useMemo, useState } from "react";
import { styled } from '@mui/material/styles';
import AdminLayout from "../components/AdminLayout";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  query,
  orderBy,
} from "firebase/firestore";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  Person as PersonIcon,
  Loyalty as LoyaltyIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  RestartAlt as RestartAltIcon,
  Download as DownloadIcon,
  People as UsersIcon,
} from "@mui/icons-material";

// Styled Components
const UsersContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const UsersHeader = styled('div')`
  margin-bottom: var(--space-lg);
`;

const PageTitle = styled('h1')`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 var(--space-sm);
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: var(--space-md);

  svg {
    width: 40px;
    height: 40px;
    color: var(--accent-primary);
  }
`;

const PageSubtitle = styled('p')`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0;
  font-weight: 400;
`;

const StyledTableContainer = styled(TableContainer)`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;

  .MuiTable-root {
    background: transparent;
  }

  .MuiTableHead-root {
    background: var(--bg-tertiary);
  }

  .MuiTableCell-head {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    font-weight: 700;
    border-bottom: 1px solid var(--border);
  }

  .MuiTableRow-root {
    border-bottom: 1px solid var(--border-subtle);

    &:hover {
      background: var(--surface-hover);
    }

    &.Mui-selected {
      background: rgba(16, 185, 129, 0.1);
    }
  }

  .MuiTableCell-body {
    color: var(--text-secondary);
    border-bottom: none;
  }
`;

// Dark theme for Material-UI components
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#10b981',
      dark: '#059669',
      light: '#34d399',
    },
    secondary: {
      main: '#8b5cf6',
    },
    success: {
      main: '#22c55e',
    },
    warning: {
      main: '#f59e0b',
    },
    error: {
      main: '#ef4444',
    },
    info: {
      main: '#06b6d4',
    },
    background: {
      default: '#0a0f0a',
      paper: '#2d342d',
    },
    text: {
      primary: '#f0f9f0',
      secondary: '#c8d8c8',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
          },
        },
        outlined: {
          borderColor: '#10b981',
          color: '#10b981',
          '&:hover': {
            borderColor: '#34d399',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          fontWeight: 600,
        },
        colorSecondary: {
          backgroundColor: 'rgba(139, 92, 246, 0.2)',
          color: '#a78bfa',
        },
        colorSuccess: {
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          color: '#22c55e',
        },
        colorDefault: {
          backgroundColor: 'rgba(156, 163, 175, 0.2)',
          color: '#9ca3af',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2d342d',
          backgroundImage: 'none',
          border: '1px solid #374151',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)',
          borderTop: '1px solid var(--border)',
        },
      },
    },
  },
});

// --- small helpers ---
const fmtDate = (d) => {
  if (!d) return "-";
  try {
    const date = typeof d === "string" ? new Date(d) : d.toDate?.() ?? new Date(d);
    return new Intl.DateTimeFormat("en-MY", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch (e) {
    return String(d);
  }
};

export default function Users() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [memberFilter, setMemberFilter] = useState("all");
  const [selection, setSelection] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [pointsDialog, setPointsDialog] = useState({ open: false, user: null, mode: "add", value: 0 });
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const q = query(collection(db, "users"), orderBy("joined_date", "desc"));
        const unsub = onSnapshot(
          q,
          (snap) => {
            const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setRows(data);
            setLoading(false);
          },
          (err) => {
            console.error('Firebase error:', err);
            setLoading(false);
            if (err.code === 'permission-denied') {
              setSnack({ open: true, msg: "Permission denied. Check Firebase rules.", severity: "error" });
            } else if (err.code === 'unavailable') {
              setSnack({ open: true, msg: "Firebase unavailable. Check connection.", severity: "error" });
            } else {
              setSnack({ open: true, msg: `Failed to load users: ${err.message}`, severity: "error" });
            }
          }
        );
        return unsub;
      } catch (error) {
        console.error('Setup error:', error);
        setLoading(false);
        setSnack({ open: true, msg: `Setup error: ${error.message}`, severity: "error" });
      }
    };

    fetchUsers();
  }, []);

  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesSearch = !s ||
        (r.name ?? "").toLowerCase().includes(s) ||
        (r.email ?? "").toLowerCase().includes(s) ||
        (r.role ?? "").toLowerCase().includes(s);
      const matchesRole = roleFilter === "all" || (r.role ?? "customer") === roleFilter;
      const isMember = Boolean(r.is_member);
      const matchesMember =
        memberFilter === "all" ||
        (memberFilter === "yes" && isMember) ||
        (memberFilter === "no" && !isMember);
      return matchesSearch && matchesRole && matchesMember;
    });
  }, [rows, search, roleFilter, memberFilter]);

  const paginatedRows = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredRows.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = paginatedRows.map(row => row.id);
      setSelection(allIds);
    } else {
      setSelection([]);
    }
  };

  const handleSelectRow = (id) => {
    const selectedIndex = selection.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selection, id];
    } else {
      newSelected = selection.filter(selectedId => selectedId !== id);
    }

    setSelection(newSelected);
  };

  const isSelected = (id) => selection.indexOf(id) !== -1;

  // --- actions ---
  const updateRole = async (user, role) => {
    try {
      await updateDoc(doc(db, "users", user.id), { role });
      setSnack({ open: true, msg: `${user.name || user.email || user.id}: role → ${role}`, severity: "success" });
    } catch (e) {
      console.error('Update role error:', e);
      setSnack({ open: true, msg: `Failed to update role: ${e.message}`, severity: "error" });
    }
  };

  const toggleMember = async (user) => {
    try {
      const next = !Boolean(user.is_member);
      await updateDoc(doc(db, "users", user.id), { is_member: next });
      setSnack({ open: true, msg: `${user.name || user.email || user.id}: member ${next ? "enabled" : "disabled"}`, severity: "success" });
    } catch (e) {
      console.error('Toggle member error:', e);
      setSnack({ open: true, msg: `Failed to update membership: ${e.message}`, severity: "error" });
    }
  };

  const applyPoints = async () => {
    const { user, mode, value } = pointsDialog;
    if (!user) return;
    const current = Number(user.points || 0);
    const valNum = Number(value || 0);
    const newPoints = mode === "set" ? valNum : current + valNum;
    try {
      await updateDoc(doc(db, "users", user.id), { points: newPoints });
      setSnack({ open: true, msg: `${user.name || user.email || user.id}: points ${mode === "set" ? "set to" : "+="} ${valNum} (now ${newPoints})`, severity: "success" });
      setPointsDialog({ open: false, user: null, mode: "add", value: 0 });
    } catch (e) {
      console.error('Apply points error:', e);
      setSnack({ open: true, msg: `Failed to update points: ${e.message}`, severity: "error" });
    }
  };

  const bulkSetMembership = async (enabled) => {
    if (selection.length === 0) return;
    try {
      await Promise.all(
        selection.map((id) => updateDoc(doc(db, "users", id), { is_member: enabled }))
      );
      setSnack({ open: true, msg: `Updated membership for ${selection.length} user(s)`, severity: "success" });
      setSelection([]);
    } catch (e) {
      console.error('Bulk membership error:', e);
      setSnack({ open: true, msg: `Failed to update membership: ${e.message}`, severity: "error" });
    }
  };

  const bulkAddPoints = async (delta) => {
    if (selection.length === 0) return;
    try {
      const byId = Object.fromEntries(rows.map((r) => [r.id, r]));
      await Promise.all(
        selection.map((id) => {
          const current = Number(byId[id]?.points || 0);
          return updateDoc(doc(db, "users", id), { points: current + delta });
        })
      );
      setSnack({ open: true, msg: `Added ${delta} point(s) to ${selection.length} user(s)`, severity: "success" });
      setSelection([]);
    } catch (e) {
      console.error('Bulk points error:', e);
      setSnack({ open: true, msg: `Failed to update points: ${e.message}`, severity: "error" });
    }
  };

  const exportCSV = () => {
    const header = ["id", "name", "email", "role", "is_member", "points", "joined_date"];
    const lines = [header.join(",")];
    filteredRows.forEach((r) => {
      const row = [
        r.id,
        JSON.stringify(r.name ?? ""),
        JSON.stringify(r.email ?? ""),
        r.role ?? "customer",
        Boolean(r.is_member),
        Number(r.points || 0),
        fmtDate(r.joined_date),
      ];
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_export_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <AdminLayout>
          <UsersContainer>
            <UsersHeader>
              <PageTitle>
                <UsersIcon />
                User & Membership Management
              </PageTitle>
              <PageSubtitle>Loading users...</PageSubtitle>
            </UsersHeader>
          </UsersContainer>
        </AdminLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <AdminLayout>
        <UsersContainer>
          <UsersHeader>
            <PageTitle>
              <UsersIcon />
              User & Membership Management
            </PageTitle>
            <PageSubtitle>
              Manage admin roles, membership status, and points. Search and filter users; perform quick actions in bulk.
            </PageSubtitle>
          </UsersHeader>

        {/* Toolbar */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
          <TextField
            size="small"
            label="Search name/email/role"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Role</Typography>
            <Select size="small" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="customer">Customer</MenuItem>
            </Select>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Member</Typography>
            <Select size="small" value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </Stack>
          <Box flex={1} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LoyaltyIcon />}
              onClick={() => bulkSetMembership(true)}
              disabled={selection.length === 0}
            >
              Set Member ({selection.length})
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LoyaltyIcon />}
              onClick={() => bulkSetMembership(false)}
              disabled={selection.length === 0}
            >
              Unset Member ({selection.length})
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => bulkAddPoints(10)}
              disabled={selection.length === 0}
            >
              +10 Points ({selection.length})
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={exportCSV}
            >
              Export CSV
            </Button>
          </Stack>
        </Stack>

        <Divider />

        {/* Custom Table instead of DataGrid */}
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selection.length > 0 && selection.length < paginatedRows.length}
                    checked={paginatedRows.length > 0 && selection.length === paginatedRows.length}
                    onChange={handleSelectAll}
                    sx={{ color: 'var(--accent-primary)' }}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Member</TableCell>
                <TableCell align="right">Points</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((user) => {
                  const isItemSelected = isSelected(user.id);
                  return (
                    <TableRow
                      key={user.id}
                      hover
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() => handleSelectRow(user.id)}
                        />
                      </TableCell>
                      <TableCell>{user.name || user.displayName || "No Name"}</TableCell>
                      <TableCell>{user.email || "No Email"}</TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={user.role || "customer"} 
                          color={(user.role === "admin") ? "secondary" : "default"} 
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={user.is_member ? "Yes" : "No"} 
                          color={user.is_member ? "success" : "default"} 
                        />
                      </TableCell>
                      <TableCell align="right">{user.points || 0}</TableCell>
                      <TableCell>{fmtDate(user.joined_date || user.createdAt || user.created_at)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title={(user.role === "admin") ? "Set as customer" : "Make admin"}>
                            <IconButton 
                              size="small" 
                              onClick={() => updateRole(user, (user.role === "admin") ? "customer" : "admin")}
                            >
                              {(user.role === "admin") ? <PersonIcon fontSize="small" /> : <AdminPanelSettingsIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={user.is_member ? "Disable membership" : "Enable membership"}>
                            <IconButton size="small" onClick={() => toggleMember(user)}>
                              <LoyaltyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add points">
                            <IconButton size="small" onClick={() => setPointsDialog({ open: true, user, mode: "add", value: 10 })}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deduct points">
                            <IconButton size="small" onClick={() => setPointsDialog({ open: true, user, mode: "add", value: -10 })}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset points to 0">
                            <IconButton size="small" onClick={() => setPointsDialog({ open: true, user, mode: "set", value: 0 })}>
                              <RestartAltIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {/* Custom Pagination */}
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />

        {/* Points dialog */}
        <Dialog open={pointsDialog.open} onClose={() => setPointsDialog({ open: false, user: null, mode: "add", value: 0 })}>
          <DialogTitle>Adjust Points</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {pointsDialog.user ? (pointsDialog.user.name || pointsDialog.user.email || pointsDialog.user.id) : ""}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Mode</Typography>
                <Select
                  size="small"
                  value={pointsDialog.mode}
                  onChange={(e) => setPointsDialog((s) => ({ ...s, mode: e.target.value }))}
                >
                  <MenuItem value="add">Add / Deduct</MenuItem>
                  <MenuItem value="set">Set absolute</MenuItem>
                </Select>
              </Stack>
              <TextField
                size="small"
                label={pointsDialog.mode === "set" ? "New points" : "Amount (+/-)"}
                type="number"
                value={pointsDialog.value}
                onChange={(e) => setPointsDialog((s) => ({ ...s, value: Number(e.target.value) }))}
                inputProps={{ step: 1 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPointsDialog({ open: false, user: null, mode: "add", value: 0 })}>Cancel</Button>
            <Button variant="contained" onClick={applyPoints}>Save</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} sx={{ width: "100%" }}>
            {snack.msg}
          </Alert>
        </Snackbar>
        </UsersContainer>
      </AdminLayout>
    </ThemeProvider>
  );
}